import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Copy, Globe, Lock, Link as LinkIcon, UserPlus, Trash2, Eye, Edit3 } from "lucide-react";

export default function ShareTab({ trip, onUpdate }) {
  const link = `${window.location.origin}/api/og/trip/${trip.share_token}`;
  const [email, setEmail] = useState("");
  const [perm, setPerm] = useState("view");
  const [busy, setBusy] = useState(false);
  const isOwner = trip._role !== "collaborator";

  const toggle = async () => {
    try { const { data } = await api.post(`/trips/${trip.trip_id}/share`); onUpdate(data); toast.success(data.share_public ? "Public link enabled" : "Trip is now private"); }
    catch { toast.error("Failed"); }
  };
  const copy = async () => { await navigator.clipboard.writeText(link); toast.success("Link copied to clipboard"); };

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.trip_id}/collaborators`, { email: email.trim(), permission: perm });
      onUpdate(data); setEmail(""); toast.success(`Invited ${email} (${perm})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (mail) => {
    try { const { data } = await api.delete(`/trips/${trip.trip_id}/collaborators/${encodeURIComponent(mail)}`); onUpdate(data); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-serif-display text-2xl font-semibold">Share &amp; collaborate</h2>

      {/* Public link */}
      <div className="rounded-2xl border border-stone-200 bg-white p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-amber-700 flex items-center gap-1"><Globe size={12}/> Public read-only link</div>
            <div className="font-serif-display text-xl font-semibold mt-1">Anyone with the link can view</div>
            <div className="text-sm text-stone-500 mt-1">Great for sharing with friends or your travel agent.</div>
          </div>
          {isOwner && <Switch checked={trip.share_public} onCheckedChange={toggle} data-testid="share-toggle"/>}
        </div>
        {trip.share_public ? (
          <div className="mt-5 flex items-center gap-2 p-3 rounded-xl bg-stone-50 border border-stone-200">
            <LinkIcon size={14} className="text-stone-500"/>
            <div className="flex-1 font-mono-code text-xs text-stone-700 truncate" data-testid="share-link-text">{link}</div>
            <Button size="sm" onClick={copy} className="bg-[#C85A32] hover:bg-[#B04B26]" data-testid="copy-share-link-button">
              <Copy size={14} className="mr-1"/> Copy
            </Button>
          </div>
        ) : (
          <div className="mt-5 flex items-center gap-2 text-sm text-stone-500">
            <Lock size={14}/> This trip is private.
          </div>
        )}
      </div>

      {/* Collaborators */}
      <div className="rounded-2xl border border-stone-200 bg-white p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-amber-700 flex items-center gap-1"><UserPlus size={12}/> Collaborators</div>
            <div className="font-serif-display text-xl font-semibold mt-1">Invite travel buddies by email</div>
            <div className="text-sm text-stone-500 mt-1">They'll see this trip in <em>Shared with me</em> when they sign in with the same email.</div>
          </div>
        </div>

        {isOwner && (
          <form onSubmit={invite} className="flex flex-wrap items-end gap-2 mb-4">
            <div className="flex-1 min-w-[220px]">
              <label className="font-mono-code text-[10px] uppercase text-stone-500">Email</label>
              <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="buddy@example.com" className="mt-1" data-testid="collab-email-input"/>
            </div>
            <div>
              <label className="font-mono-code text-[10px] uppercase text-stone-500">Permission</label>
              <select value={perm} onChange={(e)=>setPerm(e.target.value)} data-testid="collab-permission-select" className="mt-1 h-10 rounded-md border border-stone-300 bg-white px-3 text-sm">
                <option value="view">View only</option>
                <option value="edit">Can edit</option>
              </select>
            </div>
            <Button type="submit" disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="collab-invite-button">
              <UserPlus size={14} className="mr-1"/> {busy ? "Inviting…" : "Invite"}
            </Button>
          </form>
        )}

        {(trip.collaborators || []).length === 0 ? (
          <div className="text-center py-8 border border-dashed border-stone-300 rounded-lg text-stone-500 text-sm">No collaborators yet.</div>
        ) : (
          <ul className="divide-y divide-stone-100 border border-stone-200 rounded-xl overflow-hidden">
            {trip.collaborators.map(c => (
              <li key={c.email} className="p-3 flex items-center gap-3" data-testid={`collab-${c.email}`}>
                <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center font-mono-code text-xs uppercase">{c.email[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{c.email}</div>
                  <div className="text-xs text-stone-500 font-mono-code flex items-center gap-1">
                    {c.permission === "edit" ? <><Edit3 size={11}/> Can edit</> : <><Eye size={11}/> View only</>} · invited {(c.invited_at || "").slice(0,10)}
                  </div>
                </div>
                {isOwner && (
                  <button onClick={()=>remove(c.email)} data-testid={`collab-remove-${c.email}`} className="text-stone-400 hover:text-red-600 p-1"><Trash2 size={16}/></button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
